import type { FunctionComponent } from 'react';
import { useContext } from 'react';
import { NavLink, useParams } from 'react-router';

import { ActionIcon, AppShell, Group, Menu, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { CUBOH_CORPA_LINK, FEATURE_FLAGS } from 'constants/chownow';
import { HeadquarterContext } from 'context/HeadquarterContext';
import { CreateCampaignDrawer } from 'containers/AutoCampaignSettings';

import { ArrowOutIconSm, MoreIconSm } from '@chownow/pantry/icons';

import { useTranslation } from 'i18n';
import { InternalNavLink } from './InternalNavLink';

const NS = 'navInternal';

const MENU_WIDTH = 280;

export const NavInternal: FunctionComponent = () => {
  const { restaurantId } = useParams();
  const { location } = useContext(HeadquarterContext);
  const { t } = useTranslation(NS);
  const flags = useFlags();
  const showPushNotifications = !!flags?.[FEATURE_FLAGS.pushNotification];
  const [createCampaignOpened, createCampaignHandlers] = useDisclosure(false);

  // Only set to specific location if viewing a location specific page
  const corpaUrl =
    restaurantId && location?.cuboh
      ? `${CUBOH_CORPA_LINK}/list/companies/${location.cuboh.restaurant_id}`
      : CUBOH_CORPA_LINK;

  return (
    <>
      <AppShell.Header
        px={{ sm: 'sm', md: 'md', lg: 'xl' }}
        style={{
          backgroundColor: 'var(--surface-accent-primary)',
          color: 'var(--content-accent-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text variant="caption-xs" style={{ color: 'var(--content-accent-secondary)' }}>
          {t('NavInternal.label')}
        </Text>
        <Group gap="sm">
          <InternalNavLink src="/admin/restaurants" label={t('NavInternal.allRestaurants')} />
          <InternalNavLink src="/admin/orders" label={t('NavInternal.allOrders')} />
          <InternalNavLink src="/admin/customers" label={t('NavInternal.allCustomers')} />
          {/* The Menu theme pins the dropdown inline, so the `width` prop alone is ignored. */}
          <Menu
            menuItemTabIndex={0}
            position="bottom-end"
            styles={{ dropdown: { width: MENU_WIDTH } }}
          >
            <Menu.Target>
              <ActionIcon variant="link-inverse">
                <MoreIconSm />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item hiddenFrom="md" component={NavLink} to="/admin/restaurants" end>
                {t('NavInternal.allRestaurants')}
              </Menu.Item>
              <Menu.Item hiddenFrom="md" component={NavLink} to="/admin/orders" end>
                {t('NavInternal.allOrders')}
              </Menu.Item>
              <Menu.Item hiddenFrom="md" component={NavLink} to="/admin/customers" end>
                {t('NavInternal.allCustomers')}
              </Menu.Item>
              <Menu.Item component={NavLink} to="/admin/cuisines" end>
                {t('NavInternal.cuisineCategories')}
              </Menu.Item>
              {showPushNotifications && (
                <Menu.Item component={NavLink} to="/admin/push-notifications" end>
                  {t('NavInternal.pushNotifications')}
                </Menu.Item>
              )}
              <Menu.Item onClick={createCampaignHandlers.open}>
                {t('NavInternal.autoCampaignSettings')}
              </Menu.Item>
              <Menu.Item component={NavLink} to="/admin/users" end>
                {t('NavInternal.users')}
              </Menu.Item>
              <Menu.Divider color="black" />
              <Menu.Item
                rightSection={<ArrowOutIconSm />}
                component="a"
                href={corpaUrl}
                target="_blank"
              >
                {t('NavInternal.corpa')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>
      <CreateCampaignDrawer onClose={createCampaignHandlers.close} opened={createCampaignOpened} />
    </>
  );
};
